import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  Skeleton,
  Button,
  useToast,
  Tabs,
  TabsList,
  TabsTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@org/ui';
import {
  ArrowLeft,
  Plus,
  Users,
  Bell,
  BellOff,
  Pin,
  PinOff,
  MessageSquare,
  BarChart3,
  Clock,
  Flame,
} from 'lucide-react';
import { PostCard } from '@/components/post-card';
import { FloatingCreateButton } from '@/components/floating-create-button';
import { ConnectedUpcomingEvents, ConnectedTrendingPosts } from '@org/ui';
import { useState } from 'react';

export const Route = createFileRoute('/spaces/$spaceId/')({
  component: SpaceDetailPage,
});

type PostFilter = 'all' | 'discussion' | 'poll';
type SortBy = 'recent' | 'hot';

function SpaceDetailPage() {
  const { spaceId } = Route.useParams();
  const { t } = useTranslation('community');
  const { toast } = useToast();
  const [filter, setFilter] = useState<PostFilter>('all');
  const [sortBy, setSortBy] = useState<SortBy>('recent');

  const { data: space, isLoading: spaceLoading } = communityTrpc.spaces.getById.useQuery({ spaceId });
  const { data: postsData, isLoading: postsLoading } = communityTrpc.posts.listBySpace.useQuery({
    spaceId,
    limit: 50,
    sortBy,
    ...(filter !== 'all' ? { type: filter } : {}),
  });
  const { data: subStatus } = communityTrpc.spaces.isSubscribed.useQuery({ spaceId });

  const { data: pinnedData } = communityTrpc.pinnedSpaces.list.useQuery();
  const isPinned = pinnedData?.pinnedSpaces?.some((ps) => ps.spaceId === spaceId) ?? false;

  const utils = communityTrpc.useUtils();

  const subscribe = communityTrpc.spaces.subscribe.useMutation({
    onSuccess: () => {
      utils.spaces.isSubscribed.invalidate({ spaceId });
      utils.spaces.getById.invalidate({ spaceId });
      toast({ title: t('spaces.subscribed') });
    },
  });

  const unsubscribe = communityTrpc.spaces.unsubscribe.useMutation({
    onSuccess: () => {
      utils.spaces.isSubscribed.invalidate({ spaceId });
      utils.spaces.getById.invalidate({ spaceId });
    },
  });

  const pinSpace = communityTrpc.pinnedSpaces.pin.useMutation({
    onMutate: async () => {
      await utils.pinnedSpaces.list.cancel();
      const prev = utils.pinnedSpaces.list.getData();
      utils.pinnedSpaces.list.setData(undefined, (old) => {
        if (!old || !space) return old;
        return {
          pinnedSpaces: [
            ...old.pinnedSpaces,
            {
              userPinnedSpaceId: 'optimistic',
              position: old.pinnedSpaces.length + 1,
              spaceId,
              name: space.name,
              slug: space.slug ?? '',
              description: space.description ?? '',
              subPages: [],
            },
          ],
        };
      });
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) utils.pinnedSpaces.list.setData(undefined, context.prev);
    },
    onSettled: () => {
      utils.pinnedSpaces.list.invalidate();
    },
  });

  const unpinSpace = communityTrpc.pinnedSpaces.unpin.useMutation({
    onMutate: async () => {
      await utils.pinnedSpaces.list.cancel();
      const prev = utils.pinnedSpaces.list.getData();
      utils.pinnedSpaces.list.setData(undefined, (old) => {
        if (!old) return old;
        return {
          pinnedSpaces: old.pinnedSpaces.filter((ps) => ps.spaceId !== spaceId),
        };
      });
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) utils.pinnedSpaces.list.setData(undefined, context.prev);
    },
    onSettled: () => {
      utils.pinnedSpaces.list.invalidate();
    },
  });

  const posts = postsData?.posts ?? [];
  const isSubscribed = subStatus?.isSubscribed ?? false;

  const handleTogglePin = () => {
    if (isPinned) {
      unpinSpace.mutate({ spaceId });
    } else {
      pinSpace.mutate({ spaceId });
    }
  };

  const handleToggleSubscribe = () => {
    if (isSubscribed) {
      unsubscribe.mutate({ spaceId });
    } else {
      subscribe.mutate({ spaceId });
    }
  };

  if (spaceLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="rounded-2xl border border-border bg-card p-6">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="mt-2 h-4 w-full" />
        </div>
      </div>
    );
  }

  if (!space) {
    return (
      <div className="space-y-6">
        <Link to="/spaces">
          <Button variant="ghost" size="sm" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            {t('spaces.backToSpaces')}
          </Button>
        </Link>
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <p className="text-muted-foreground">{t('spaces.notFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link to="/spaces">
        <Button variant="ghost" size="sm" className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          {t('spaces.backToSpaces')}
        </Button>
      </Link>

      {/* Space Header */}
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 sm:flex-1">
            <h1 className="text-2xl font-bold">{space.name}</h1>
            {space.description && (
              <p className="mt-2 text-muted-foreground">{space.description}</p>
            )}
            <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" aria-hidden="true" />
              <span>{space.subscriberCount} {t('spaces.members')}</span>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
            <Button
              variant={isSubscribed ? 'outline' : 'default'}
              onClick={handleToggleSubscribe}
              disabled={subscribe.isPending || unsubscribe.isPending}
              className="gap-2"
            >
              {isSubscribed ? (
                <>
                  <BellOff className="h-4 w-4" />
                  {t('spaces.subscribed')}
                </>
              ) : (
                <>
                  <Bell className="h-4 w-4" />
                  {t('spaces.subscribe')}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleTogglePin}
              disabled={pinSpace.isPending || unpinSpace.isPending}
              className="gap-2"
              aria-label={isPinned ? t('spaces.unpin') : t('spaces.pin')}
            >
              {isPinned ? (
                <>
                  <PinOff className="h-4 w-4" />
                  {t('spaces.unpin')}
                </>
              ) : (
                <>
                  <Pin className="h-4 w-4" />
                  {t('spaces.pin')}
                </>
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="gap-2">
                  <Plus className="h-4 w-4" />
                  {t('feed.newPost')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/posts/new" search={{ type: 'discussion', spaceId }}>
                    <MessageSquare className="mr-2 h-4 w-4" />
                    {t('spaces.newDiscussion')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/posts/new" search={{ type: 'poll', spaceId }}>
                    <BarChart3 className="mr-2 h-4 w-4" />
                    {t('spaces.newPoll')}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Content + Right Rail */}
      <div className="flex gap-6">
        {/* Main feed */}
        <div className="min-w-0 flex-1 space-y-6">
          {/* Filter Tabs + Sort */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Tabs value={filter} onValueChange={(v) => setFilter(v as PostFilter)}>
              <TabsList>
                <TabsTrigger value="all">{t('spaces.filterAll')}</TabsTrigger>
                <TabsTrigger value="discussion">{t('spaces.filterDiscussions')}</TabsTrigger>
                <TabsTrigger value="poll">{t('spaces.filterPolls')}</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex gap-2" role="group" aria-label={t('feed.sortBy')}>
              <Button
                variant={sortBy === 'recent' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSortBy('recent')}
                aria-pressed={sortBy === 'recent'}
                className="gap-1.5"
              >
                <Clock className="h-4 w-4" aria-hidden="true" />
                {t('feed.newest')}
              </Button>
              <Button
                variant={sortBy === 'hot' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSortBy('hot')}
                aria-pressed={sortBy === 'hot'}
                className="gap-1.5"
              >
                <Flame className="h-4 w-4" aria-hidden="true" />
                {t('feed.hottest')}
              </Button>
            </div>
          </div>

          {/* Posts */}
          <div className="space-y-4">
            {postsLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-border bg-card p-4">
                  <div className="flex items-start gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  </div>
                </div>
              ))
            ) : posts.length === 0 ? (
              <div className="rounded-2xl border border-border bg-card p-8 text-center">
                <MessageSquare className="mx-auto h-12 w-12 text-muted-foreground" aria-hidden="true" />
                <p className="mt-4 text-muted-foreground">{t('spaces.emptyPosts')}</p>
                <p className="mt-1 text-sm text-muted-foreground">{t('spaces.beFirstToPost')}</p>
              </div>
            ) : (
              posts.map((post) => <PostCard key={post.postId} post={post as any} />)
            )}
          </div>
        </div>

        {/* Right rail — lg+ only */}
        <aside className="hidden w-72 shrink-0 lg:block" aria-label={t('feed.rightRail')}>
          <div className="sticky top-4 space-y-4">
            <ConnectedUpcomingEvents
              viewAllHref="/events"
              title={t('rightRail.upcomingEvents', { defaultValue: 'Upcoming events' })}
              emptyLabel={t('rightRail.noEvents', { defaultValue: 'No upcoming events' })}
              viewAllLabel={t('rightRail.viewAll', { defaultValue: 'View all' })}
              labels={{
                today: t('rightRail.today'),
                tomorrow: t('rightRail.tomorrow'),
                startsInMinutes: (count) => t('rightRail.startsInMinutes', { count }),
                startsAt: (time) => t('rightRail.startsAt', { time }),
                kind: {
                  group: t('rightRail.kind.group'),
                  personal: t('rightRail.kind.personal'),
                  event: t('rightRail.kind.event'),
                },
              }}
            />
            <ConnectedTrendingPosts
              viewAllHref="/search"
              title={t('rightRail.trendingPosts', { defaultValue: 'Trending posts' })}
              emptyLabel={t('rightRail.noPosts', { defaultValue: 'No trending posts' })}
              viewAllLabel={t('rightRail.viewAll', { defaultValue: 'View all' })}
            />
          </div>
        </aside>
      </div>

      <FloatingCreateButton spaceId={spaceId} />
    </div>
  );
}
